import type { BigFiveTraits, MBTIType } from '../types';
import type { FreeResultId, PaidResultId, ResultId, ResultReading } from '../types/results';
import { nearestArchetype, validTraits, type Archetype } from './archetypes';
import {
  friendsCharacters,
  hogwartsHouses,
  myersBriggsTypes,
  sexAndTheCityCharacters,
} from './archetypeSets';

/** Every result past the free ones unlocks together, once. */
export const RESULTS_PRICE = { amount: 499, currency: 'usd' } as const;

interface ResultDefinition<Id extends ResultId> {
  id: Id;
  title: string;
  question: string;
}

export const freeResults: ResultDefinition<FreeResultId>[] = [
  { id: 'sex-and-the-city', title: 'Sex and the City', question: 'Which character are you?' },
  { id: 'hogwarts', title: 'Hogwarts', question: 'Which house are you?' },
  { id: 'myers-briggs', title: 'Myers-Briggs', question: 'What’s your type?' },
  { id: 'big-five', title: 'Big Five', question: 'How do your traits stack up?' },
  { id: 'friends', title: 'Friends', question: 'Which friend are you?' },
];

/** Paid results are chosen by the model from these answers only, after someone pays. */
export const paidResults: (ResultDefinition<PaidResultId> & { options: readonly string[] })[] = [
  {
    id: 'enneagram',
    title: 'Enneagram',
    question: 'What drives you?',
    options: [
      'Type 1 · The Improver',
      'Type 2 · The Giver',
      'Type 3 · The Achiever',
      'Type 4 · The Individualist',
      'Type 5 · The Observer',
      'Type 6 · The Loyalist',
      'Type 7 · The Enthusiast',
      'Type 8 · The Challenger',
      'Type 9 · The Peacemaker',
    ],
  },
  {
    id: 'love-language',
    title: 'Love language',
    question: 'How do you show you care?',
    options: ['Words of affirmation', 'Quality time', 'Acts of service', 'Gifts', 'Physical touch'],
  },
  {
    id: 'attachment-style',
    title: 'Attachment style',
    question: 'How do you connect?',
    options: ['Secure', 'Anxious', 'Avoidant', 'Anxious-avoidant'],
  },
  {
    id: 'the-office',
    title: 'The Office',
    question: 'Which coworker are you?',
    options: ['Michael', 'Dwight', 'Jim', 'Pam', 'Angela', 'Kevin', 'Oscar', 'Kelly'],
  },
  {
    id: 'star-sign',
    title: 'Star sign',
    question: 'Which sign do you text like?',
    options: [
      'Aries',
      'Taurus',
      'Gemini',
      'Cancer',
      'Leo',
      'Virgo',
      'Libra',
      'Scorpio',
      'Sagittarius',
      'Capricorn',
      'Aquarius',
      'Pisces',
    ],
  },
  {
    id: 'taylor-swift-era',
    title: 'Taylor Swift',
    question: 'Which era are you in?',
    options: [
      'Debut',
      'Fearless',
      'Speak Now',
      'Red',
      '1989',
      'Reputation',
      'Lover',
      'Folklore',
      'Evermore',
      'Midnights',
      'The Tortured Poets Department',
      'The Life of a Showgirl',
    ],
  },
  {
    id: 'disney',
    title: 'Disney',
    question: 'Which Disney character are you?',
    options: ['Belle', 'Ariel', 'Mulan', 'Moana', 'Elsa', 'Anna', 'Rapunzel', 'Simba', 'Stitch'],
  },
  {
    id: 'game-of-thrones',
    title: 'Game of Thrones',
    question: 'Which character are you?',
    options: [
      'Jon Snow',
      'Daenerys Targaryen',
      'Tyrion Lannister',
      'Arya Stark',
      'Sansa Stark',
      'Cersei Lannister',
      'Brienne of Tarth',
      'Samwell Tarly',
    ],
  },
];

export const resultCatalog = [...freeResults, ...paidResults];

export const archetypeSets = {
  'sex-and-the-city': sexAndTheCityCharacters,
  hogwarts: hogwartsHouses,
  friends: friendsCharacters,
} as const;

type ArchetypeResult = keyof typeof archetypeSets;

/** The line a given match opens with, so marketing quotes exactly what people are told. */
export function archetypeLine<R extends ArchetypeResult>(
  result: R,
  id: (typeof archetypeSets)[R][number]['id']
): string {
  const match = (archetypeSets[result] as readonly Archetype[]).find((item) => item.id === id);
  if (!match) throw new Error(`Unknown ${result} match: ${id}`);
  return match.line;
}

interface Trait {
  word: string;
  phrase: string;
  line: string;
}

const traitWords: Record<keyof BigFiveTraits, { label: string; high: Trait; low: Trait }> = {
  openness: {
    label: 'Openness',
    high: {
      word: 'Curious',
      phrase: 'your curiosity',
      line: 'You asked a follow-up question to the follow-up question.',
    },
    low: {
      word: 'Grounded',
      phrase: 'your practicality',
      line: 'You picked the place with the good reviews. Again.',
    },
  },
  conscientiousness: {
    label: 'Conscientiousness',
    high: {
      word: 'Dependable',
      phrase: 'your reliability',
      line: 'You replied the same day. Every time.',
    },
    low: {
      word: 'Spontaneous',
      phrase: 'your spontaneity',
      line: 'You said yes to the trip before checking the dates.',
    },
  },
  extraversion: {
    label: 'Extraversion',
    high: {
      word: 'Outgoing',
      phrase: 'your social energy',
      line: 'You have a group chat for your group chats.',
    },
    low: {
      word: 'Reflective',
      phrase: 'your reflective streak',
      line: 'You read the whole thread before saying anything.',
    },
  },
  agreeableness: {
    label: 'Agreeableness',
    high: {
      word: 'Warm',
      phrase: 'your warmth',
      line: 'You end most messages with a heart, and you mean it.',
    },
    low: {
      word: 'Candid',
      phrase: 'your candor',
      line: 'You say what everyone was thinking, just faster.',
    },
  },
  neuroticism: {
    label: 'Emotional sensitivity',
    high: {
      word: 'Sensitive',
      phrase: 'how deeply you feel things',
      line: 'You noticed the period at the end of their ‘ok.’',
    },
    low: {
      word: 'Steady',
      phrase: 'your steadiness',
      line: 'You were the calm one in the chaotic group chat.',
    },
  },
};
function bigFiveReading(traits: BigFiveTraits): ResultReading {
  const standout = (Object.keys(traitWords) as (keyof BigFiveTraits)[])
    .map((key) => ({ key, distance: Math.abs(traits[key] - 50) }))
    .sort((a, b) => b.distance - a.distance)
    .slice(0, 2)
    .map(({ key }) => traitWords[key][traits[key] >= 50 ? 'high' : 'low']);
  return {
    id: 'big-five',
    answer: `${standout[0].word} & ${standout[1].word}`,
    tagline: 'Your five traits, from how you text',
    description: `Of your five traits, ${standout[0].phrase} and ${standout[1].phrase} stand out most.`,
    line: standout[0].line,
    scores: (Object.keys(traitWords) as (keyof BigFiveTraits)[]).map((key) => ({
      label: traitWords[key].label,
      value: Math.round(traits[key]),
    })),
  };
}

function archetypeReading(id: ArchetypeResult, traits: BigFiveTraits): ResultReading | null {
  const match = nearestArchetype<Archetype>(traits, archetypeSets[id]);
  return (
    match && {
      id,
      answer: match.name,
      matchId: match.id,
      tagline: match.tagline,
      description: match.description,
      line: match.line,
    }
  );
}

// Sixteen letter combinations, all of them named in myersBriggsTypes.
const isMBTIType = (value: unknown): value is MBTIType =>
  typeof value === 'string' && /^[EI][NS][TF][JP]$/.test(value);

/** The free results, worked out from the personality profile alone. */
export function computeFreeResults(profile: {
  bigFive: BigFiveTraits;
  mbti?: unknown;
}): ResultReading[] {
  if (!validTraits(profile.bigFive)) return [];
  const mbti = isMBTIType(profile.mbti) ? profile.mbti : null;
  const readings: Record<FreeResultId, ResultReading | null> = {
    'sex-and-the-city': archetypeReading('sex-and-the-city', profile.bigFive),
    hogwarts: archetypeReading('hogwarts', profile.bigFive),
    'myers-briggs': mbti && {
      id: 'myers-briggs',
      answer: mbti,
      matchId: mbti,
      tagline: myersBriggsTypes[mbti].name,
      description: myersBriggsTypes[mbti].description,
      line: myersBriggsTypes[mbti].line,
    },
    'big-five': bigFiveReading(profile.bigFive),
    friends: archetypeReading('friends', profile.bigFive),
  };
  return freeResults.flatMap(({ id }) => readings[id] ?? []);
}
